import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { from, of } from 'rxjs';
import { injectQueryClient } from '@tanstack/angular-query-experimental';

import { catchError, map, switchMap } from 'rxjs/operators';
import { requireSession } from './auth.guard';
import { SessionService, isRejectedCredential } from '../services/session.service';
import type { UserSession } from '../types/session';
import { UserService } from '../services/user.service';

export const upgradeGuard: CanActivateFn = (_route, state) => {
  const router = inject(Router);
  const sessionService = inject(SessionService);
  const userService = inject(UserService);
  const queryClient = injectQueryClient();

  return requireSession(state.url).pipe(
    switchMap(canActivate => {
      if (!canActivate) return of(false);

      const session = sessionService.sessionSnapshot;
      const options = userService.refreshSessionQueryOptions();
      const invalidated = queryClient.getQueryState(options.queryKey)?.isInvalidated;
      // Billing changes invalidate the session query. Resolve those and upgrade entry
      // against the server; ordinary page changes use the current session immediately.
      const mustRefresh = !session || invalidated || isUpgradeRoute(state.url);
      if (!mustRefresh) {
        // fetchQuery deduplicates refreshes and respects the session's stale time.
        // A background network failure must not interrupt an otherwise usable page.
        void queryClient.fetchQuery(options).catch((): void => undefined);
      }

      return (mustRefresh ? from(queryClient.fetchQuery({ ...options, staleTime: 0 })) : of(session)).pipe(
        map(current => decide(current, state.url, router)),
        catchError((error: unknown) => {
          // A transient failure (the refresh runs even when TanStack thinks the app is
          // offline) must not sign out a valid user; route from the cached session.
          if (session && !isRejectedCredential(error)) return of(decide(session, state.url, router));
          sessionService.clearSession();
          void router.navigate(['/login', { redirectTo: state.url }]);
          return of(false);
        }),
      );
    }),
  );
};

function decide(session: UserSession, url: string, router: Router): boolean {
  const membershipState = session.account.membershipState;
  const isUpgradeEntry = isUpgradeRoute(url);

  if (membershipState === 'lapsed_grace') {
    void router.navigate(['resubscribe']);
    return false;
  }

  if (membershipState === 'lapsed_expired' || membershipState === 'free_never_member') {
    void router.navigate(['getting-started']);
    return false;
  }

  const hasBankAccountSyncAccess = session.enabledFeatures.some(feature => feature === 'bank-account-sync');

  if (isUpgradeEntry) {
    if (membershipState === 'active' || hasBankAccountSyncAccess) {
      void router.navigate(['budgets']);
      return false;
    }

    return true;
  }

  if (membershipState === 'active' || hasBankAccountSyncAccess) return true;
  void router.navigate(['upgrade']);
  return false;
}

function isUpgradeRoute(url: string): boolean {
  return url.split(/[?#]/, 1)[0] === '/upgrade';
}
